import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff, Package, TruckIcon, FileText, BarChart3, ArrowLeft, KeyRound, ShieldCheck, QrCode, Smartphone } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import logoMark from '../assets/logo-mark.png';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { roleHome } from '../constants/roleAccess';
import { useTranslation, useLanguage } from '../hooks/useTranslation';
import { api } from '../config/api';

interface LoginForm {
  email: string;
  password: string;
}

// ── Slides data ────────────────────────────────────────────────────────────────
const SLIDES_FR = [
  {
    icon: Package,
    title: 'Suivi en Temps Réel',
    description: "Localisez chaque colis à chaque étape — de l'expédition à la livraison — avec un historique complet.",
    gradient: 'from-violet-600 to-indigo-600',
    accent: '#8B5CF6',
  },
  {
    icon: TruckIcon,
    title: 'Gestion de Flotte',
    description: 'Assignez des livreurs, suivez leurs performances et optimisez vos tournées en quelques clics.',
    gradient: 'from-indigo-600 to-cyan-600',
    accent: '#6366F1',
  },
  {
    icon: FileText,
    title: 'Facturation Automatique',
    description: 'Générez des factures PDF professionnelles, exportez vos données en Excel et gérez vos finances.',
    gradient: 'from-cyan-600 to-teal-600',
    accent: '#0891B2',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Rapports',
    description: 'Tableaux de bord avancés, KPIs en temps réel et rapports détaillés pour piloter votre activité.',
    gradient: 'from-teal-600 to-violet-600',
    accent: '#0D9488',
  },
];

const SLIDES_EN = [
  {
    icon: Package,
    title: 'Real-Time Tracking',
    description: 'Locate every parcel at every step — from dispatch to delivery — with a complete history.',
    gradient: 'from-violet-600 to-indigo-600',
    accent: '#8B5CF6',
  },
  {
    icon: TruckIcon,
    title: 'Fleet Management',
    description: 'Assign drivers, track their performance and optimise your routes in a few clicks.',
    gradient: 'from-indigo-600 to-cyan-600',
    accent: '#6366F1',
  },
  {
    icon: FileText,
    title: 'Automated Billing',
    description: 'Generate professional PDF invoices, export data to Excel and manage your finances effortlessly.',
    gradient: 'from-cyan-600 to-teal-600',
    accent: '#0891B2',
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reports',
    description: 'Advanced dashboards, real-time KPIs and detailed reports to steer your business.',
    gradient: 'from-teal-600 to-violet-600',
    accent: '#0D9488',
  },
];

type ResetStep = 'login' | 'forgot' | 'otp' | 'done';
type LoginStage = 'credentials' | 'totp-validate' | 'totp-setup';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setAuth, setLoginNotification } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const t = useTranslation();
  const { language } = useLanguage();
  const isEn = language === 'en';
  const slidesData = isEn ? SLIDES_EN : SLIDES_FR;

  // ── TOTP state ────────────────────────────────────────────────────────────
  const [loginStage, setLoginStage] = useState<LoginStage>('credentials');
  const [totpSessionToken, setTotpSessionToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpError, setTotpError] = useState('');
  const [setupData, setSetupData] = useState<{ qr_data_url: string; secret: string } | null>(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const totpInputRef = useRef<HTMLInputElement>(null);

  // ── Password reset state ──────────────────────────────────────────────────
  const [resetStep, setResetStep] = useState<ResetStep>('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPwd, setResetNewPwd] = useState('');
  const [resetPhoneHint, setResetPhoneHint] = useState<string | undefined>();
  const [resetLoading, setResetLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slidesData.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slidesData.length]);

  const finishLogin = (response: any) => {
    setAuth(response.user, response.access_token, response.refresh_token);
    setLoginNotification({
      lastLoginAt: response.last_login_at ?? null,
      failedAttempts: response.failed_attempts ?? 0,
    });
    toast.success(t.login.success);
    navigate(roleHome(response.user.role));
  };

  const onSubmit = async (data: LoginForm) => {
    try {
      setLoading(true);
      const response = await authService.login(data.email, data.password);

      if (response.requires_totp_setup) {
        // Force TOTP setup — fetch QR
        setTotpSessionToken(response.totp_session_token!);
        setSetupLoading(true);
        try {
          const setupResp = await authService.setupForcedTotp(response.totp_session_token!);
          setSetupData({ qr_data_url: setupResp.qr_data_url, secret: setupResp.secret });
          setLoginStage('totp-setup');
          setTimeout(() => totpInputRef.current?.focus(), 300);
        } catch {
          toast.error('Erreur lors de la génération du QR code. Réessayez.');
        } finally {
          setSetupLoading(false);
        }
        return;
      }

      if (response.requires_totp) {
        // TOTP challenge
        setTotpSessionToken(response.totp_session_token!);
        setTotpCode('');
        setTotpError('');
        setLoginStage('totp-validate');
        setTimeout(() => totpInputRef.current?.focus(), 300);
        return;
      }

      // No TOTP (non-admin role)
      finishLogin(response);
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || t.login.error);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpValidate = async () => {
    if (totpCode.length !== 6) return;
    setTotpLoading(true);
    setTotpError('');
    try {
      const response = await authService.validateTotpLogin(totpSessionToken, totpCode);
      finishLogin(response);
    } catch (error: any) {
      setTotpError(error.response?.data?.message || 'Code invalide. Réessayez.');
      setTotpCode('');
      setTimeout(() => totpInputRef.current?.focus(), 100);
    } finally {
      setTotpLoading(false);
    }
  };

  const handleTotpSetupVerify = async () => {
    if (totpCode.length !== 6) return;
    setTotpLoading(true);
    setTotpError('');
    try {
      const response = await authService.verifySetupForcedTotp(totpSessionToken, totpCode);
      toast.success(isEn ? '2FA enabled successfully!' : '2FA activé avec succès !');
      finishLogin(response);
    } catch (error: any) {
      setTotpError(error.response?.data?.message || 'Code invalide. Vérifiez l\'heure de votre appareil.');
      setTotpCode('');
      setTimeout(() => totpInputRef.current?.focus(), 100);
    } finally {
      setTotpLoading(false);
    }
  };

  const handleForgotSubmit = async () => {
    if (!resetEmail.trim()) return;
    try {
      setResetLoading(true);
      const res = await api.post('/auth/forgot-password', { email: resetEmail });
      setResetPhoneHint(res.data.phone_hint);
      setResetStep('otp');
      toast.success(isEn ? 'Code sent via SMS' : 'Code envoyé par SMS');
    } catch {
      toast.error(isEn ? 'Error sending code' : 'Erreur lors de l\'envoi');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetSubmit = async () => {
    if (!resetOtp.trim() || resetNewPwd.length < 6) return;
    try {
      setResetLoading(true);
      await api.post('/auth/reset-password', { email: resetEmail, otp: resetOtp, new_password: resetNewPwd });
      setResetStep('done');
      toast.success(isEn ? 'Password updated!' : 'Mot de passe mis à jour !');
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isEn ? 'Invalid or expired code' : 'Code invalide ou expiré'));
    } finally {
      setResetLoading(false);
    }
  };

  const slide = slidesData[activeSlide];
  const SlideIcon = slide.icon;

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel — Login form ──────────────────────────────────────────── */}
      <div className="relative flex flex-col justify-center w-full lg:w-[45%] min-h-screen bg-[#0a0515] px-8 md:px-16 py-12">
        {/* Subtle background glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-violet-600/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -right-10 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-md mx-auto">
          {/* Language switcher */}
          <div className="absolute -top-6 right-0">
            <LanguageSwitcher />
          </div>

          {/* Logo */}
          <div className="mb-10">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-600/30">
                <img src={logoMark} alt="CeDo Trace" className="w-7 h-7 object-contain" />
              </div>
              <span className="text-white font-bold text-xl tracking-tight">CEDO Trace</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">{t.login.subtitle}</h1>
            <p className="text-white/50 text-sm">
              {isEn
                ? 'Enter your credentials to access your workspace'
                : 'Entrez vos identifiants pour accéder à votre espace'}
            </p>
          </div>

          {/* ── TOTP validate panel ───────────────────────────────────────────── */}
          {loginStage === 'totp-validate' && (
            <div className="space-y-5">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-violet-500/20 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-8 h-8 text-violet-400" />
                </div>
                <h2 className="text-white font-bold text-xl mb-1">
                  {isEn ? 'Two-Step Verification' : 'Vérification en deux étapes'}
                </h2>
                <p className="text-white/50 text-sm max-w-xs">
                  {isEn
                    ? 'Enter the 6-digit code from your Google or Microsoft Authenticator app'
                    : 'Entrez le code à 6 chiffres affiché dans Google Authenticator ou Microsoft Authenticator'}
                </p>
              </div>

              <div>
                <input
                  ref={totpInputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    setTotpCode(v);
                    setTotpError('');
                    if (v.length === 6) {
                      // auto-submit
                      setTimeout(() => {
                        authService.validateTotpLogin(totpSessionToken, v).then(finishLogin).catch((err: any) => {
                          setTotpError(err.response?.data?.message || 'Code invalide. Réessayez.');
                          setTotpCode('');
                        });
                      }, 0);
                    }
                  }}
                  placeholder="000000"
                  className="w-full py-4 rounded-xl text-white text-3xl placeholder-white/20 bg-white/5 border border-white/10 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all tracking-[0.5em] text-center font-mono"
                />
                {totpError && <p className="mt-2 text-red-400 text-xs text-center">{totpError}</p>}
              </div>

              <button
                type="button"
                onClick={handleTotpValidate}
                disabled={totpCode.length !== 6 || totpLoading}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-semibold text-sm disabled:opacity-50 transition-all"
              >
                {totpLoading ? (isEn ? 'Verifying…' : 'Vérification…') : (isEn ? 'Verify' : 'Valider')}
              </button>

              <p className="text-center text-white/30 text-xs">
                {isEn ? 'Open your authenticator app to find your code.' : 'Ouvrez votre application pour voir le code.'}
              </p>
            </div>
          )}

          {/* ── TOTP setup panel (forced) ──────────────────────────────────────── */}
          {loginStage === 'totp-setup' && (
            <div className="space-y-5">
              <div className="flex flex-col items-center text-center mb-4">
                <div className="w-16 h-16 rounded-2xl bg-cyan-500/20 flex items-center justify-center mb-4">
                  <Smartphone className="w-8 h-8 text-cyan-400" />
                </div>
                <h2 className="text-white font-bold text-xl mb-1">
                  {isEn ? 'Set Up Two-Factor Authentication' : 'Configurer l\'authentification à deux facteurs'}
                </h2>
                <p className="text-white/50 text-sm max-w-xs">
                  {isEn
                    ? 'Required for your account. Scan the QR code with Google or Microsoft Authenticator, then enter the code below.'
                    : 'Obligatoire pour votre compte. Scannez le QR code avec Google ou Microsoft Authenticator, puis entrez le code ci-dessous.'}
                </p>
              </div>

              {setupLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 rounded-full border-2 border-violet-400 border-t-transparent animate-spin" />
                </div>
              ) : setupData ? (
                <>
                  {/* Step indicator */}
                  <div className="flex items-center gap-3 justify-center text-xs text-white/40 mb-2">
                    <span className="flex items-center gap-1"><QrCode className="w-3 h-3" /> {isEn ? '1. Scan QR' : '1. Scanner le QR'}</span>
                    <span className="text-white/20">•</span>
                    <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {isEn ? '2. Enter code' : '2. Entrer le code'}</span>
                  </div>

                  {/* QR code */}
                  <div className="flex justify-center">
                    <div className="p-3 bg-white rounded-2xl">
                      <img src={setupData.qr_data_url} alt="QR Code 2FA" className="w-48 h-48" />
                    </div>
                  </div>

                  {/* Manual secret */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowSecret(!showSecret)}
                      className="w-full text-white/30 hover:text-white/60 text-xs text-center transition-colors underline"
                    >
                      {showSecret
                        ? (isEn ? 'Hide manual key' : 'Masquer la clé manuelle')
                        : (isEn ? 'Can\'t scan? Enter key manually' : 'Impossible de scanner ? Saisir la clé manuellement')}
                    </button>
                    {showSecret && (
                      <div className="mt-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-center">
                        <p className="text-white/50 text-xs mb-1">{isEn ? 'Manual key:' : 'Clé manuelle :'}</p>
                        <p className="text-white font-mono text-sm tracking-widest break-all">{setupData.secret}</p>
                      </div>
                    )}
                  </div>

                  {/* Code input */}
                  <div>
                    <label className="block text-white/60 text-xs mb-2 text-center">
                      {isEn ? 'Enter the 6-digit code from your app:' : 'Entrez le code à 6 chiffres de l\'application :'}
                    </label>
                    <input
                      ref={totpInputRef}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={totpCode}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '');
                        setTotpCode(v);
                        setTotpError('');
                      }}
                      placeholder="000000"
                      className="w-full py-4 rounded-xl text-white text-3xl placeholder-white/20 bg-white/5 border border-white/10 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all tracking-[0.5em] text-center font-mono"
                    />
                    {totpError && <p className="mt-2 text-red-400 text-xs text-center">{totpError}</p>}
                  </div>

                  <button
                    type="button"
                    onClick={handleTotpSetupVerify}
                    disabled={totpCode.length !== 6 || totpLoading}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500 text-white font-semibold text-sm disabled:opacity-50 transition-all"
                  >
                    {totpLoading ? (isEn ? 'Activating…' : 'Activation…') : (isEn ? 'Activate & Continue' : 'Activer et continuer')}
                  </button>
                </>
              ) : null}
            </div>
          )}

          {/* ── Password reset panels ──────────────────────────────────────────── */}
          {loginStage === 'credentials' && resetStep === 'forgot' && (
            <div className="space-y-4">
              <button type="button" onClick={() => setResetStep('login')} className="flex items-center gap-2 text-white/50 hover:text-white/80 text-sm transition-colors mb-2">
                <ArrowLeft className="w-4 h-4" /> {isEn ? 'Back' : 'Retour'}
              </button>
              <h2 className="text-white font-semibold text-lg">{isEn ? 'Reset your password' : 'Réinitialiser le mot de passe'}</h2>
              <p className="text-white/50 text-sm">{isEn ? 'Enter your email to receive a 6-digit SMS code.' : 'Entrez votre email pour recevoir un code SMS à 6 chiffres.'}</p>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="vous@entreprise.com"
                  className="w-full pl-11 pr-4 py-3 rounded-xl text-white text-sm placeholder-white/25 bg-white/5 border border-white/10 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all"
                />
              </div>
              <button type="button" onClick={handleForgotSubmit} disabled={!resetEmail.trim() || resetLoading}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-semibold text-sm disabled:opacity-50 transition-all">
                {resetLoading ? '…' : (isEn ? 'Send code' : 'Envoyer le code')}
              </button>
            </div>
          )}

          {loginStage === 'credentials' && resetStep === 'otp' && (
            <div className="space-y-4">
              <button type="button" onClick={() => setResetStep('forgot')} className="flex items-center gap-2 text-white/50 hover:text-white/80 text-sm transition-colors mb-2">
                <ArrowLeft className="w-4 h-4" /> {isEn ? 'Back' : 'Retour'}
              </button>
              <h2 className="text-white font-semibold text-lg">{isEn ? 'Enter the code' : 'Entrez le code'}</h2>
              {resetPhoneHint && (
                <p className="text-white/50 text-sm">
                  {isEn ? `Code sent to ${resetPhoneHint}` : `Code envoyé au ${resetPhoneHint}`}
                </p>
              )}
              <div className="relative">
                <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  maxLength={6}
                  value={resetOtp}
                  onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full pl-11 pr-4 py-3 rounded-xl text-white text-sm placeholder-white/25 bg-white/5 border border-white/10 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all tracking-widest text-center font-mono text-lg"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="password"
                  value={resetNewPwd}
                  onChange={(e) => setResetNewPwd(e.target.value)}
                  placeholder={isEn ? 'New password (min 6 chars)' : 'Nouveau mot de passe (min 6 car.)'}
                  className="w-full pl-11 pr-4 py-3 rounded-xl text-white text-sm placeholder-white/25 bg-white/5 border border-white/10 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all"
                />
              </div>
              <button type="button" onClick={handleResetSubmit} disabled={resetOtp.length !== 6 || resetNewPwd.length < 6 || resetLoading}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-semibold text-sm disabled:opacity-50 transition-all">
                {resetLoading ? '…' : (isEn ? 'Reset password' : 'Réinitialiser')}
              </button>
            </div>
          )}

          {loginStage === 'credentials' && resetStep === 'done' && (
            <div className="text-center space-y-4 py-6">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                <KeyRound className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-white font-semibold">{isEn ? 'Password updated!' : 'Mot de passe mis à jour !'}</p>
              <p className="text-white/50 text-sm">{isEn ? 'You can now sign in with your new password.' : 'Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.'}</p>
              <button type="button" onClick={() => { setResetStep('login'); setResetOtp(''); setResetNewPwd(''); setResetEmail(''); }}
                className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm transition-all">
                {isEn ? 'Back to sign in' : 'Retour à la connexion'}
              </button>
            </div>
          )}

          {/* Form */}
          {loginStage === 'credentials' && resetStep === 'login' && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                {t.login.email}
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-white/30" />
                <input
                  type="email"
                  autoComplete="email"
                  className={`w-full pl-11 pr-4 py-3 rounded-xl text-white text-sm placeholder-white/25
                    bg-white/5 border transition-all outline-none
                    focus:border-violet-500 focus:bg-white/8 focus:ring-2 focus:ring-violet-500/20
                    ${errors.email ? 'border-red-500/60' : 'border-white/10'}`}
                  placeholder="vous@entreprise.com"
                  {...register('email', {
                    required: t.login.emailRequired,
                    pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: t.login.emailInvalid },
                  })}
                />
              </div>
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-400">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                {t.login.password}
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-white/30" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`w-full pl-11 pr-11 py-3 rounded-xl text-white text-sm placeholder-white/25
                    bg-white/5 border transition-all outline-none
                    focus:border-violet-500 focus:bg-white/8 focus:ring-2 focus:ring-violet-500/20
                    ${errors.password ? 'border-red-500/60' : 'border-white/10'}`}
                  placeholder="••••••••"
                  {...register('password', { required: t.login.passwordRequired })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? (isEn ? 'Hide password' : 'Masquer') : (isEn ? 'Show password' : 'Afficher')}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-400">{errors.password.message}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 mt-2 rounded-xl font-semibold text-sm text-white
                bg-gradient-to-r from-violet-600 to-indigo-600
                hover:from-violet-500 hover:to-indigo-500
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all transform hover:scale-[1.01] active:scale-[0.99]
                shadow-lg shadow-violet-600/30"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {isEn ? 'Signing in…' : 'Connexion…'}
                </span>
              ) : (
                t.login.submit
              )}
            </button>

            {/* Forgot password + phone login */}
            <div className="text-center mt-4 flex flex-col items-center gap-2">
              <Link
                to="/login-telephone"
                className="text-white/70 hover:text-white text-sm underline transition-colors"
              >
                {isEn ? 'Sign in with a phone code (senders)' : 'Se connecter par téléphone (expéditeurs)'}
              </Link>
              <button
                type="button"
                onClick={() => { setResetStep('forgot'); setResetEmail(''); }}
                className="text-white/40 hover:text-white/70 text-xs underline transition-colors"
              >
                {isEn ? 'Forgot your password?' : 'Mot de passe oublié ?'}
              </button>
            </div>

            {/* Sign-up links */}
            <div className="text-center mt-3 text-white/30 text-xs">
              {isEn ? "Don't have an account?" : "Pas encore de compte ?"}{' '}
              <Link to="/register-expediteur" className="text-white/60 hover:text-white underline">
                {isEn ? 'Sign up' : "S'inscrire"}
              </Link>
              {' · '}
              <Link to="/register" className="text-white/60 hover:text-white underline">
                {isEn ? 'Register your company' : 'Inscrire mon entreprise'}
              </Link>
            </div>
          </form>
          )}

          {/* Footer */}
          {loginStage === 'credentials' && resetStep === 'login' && (
            <p className="text-center text-white/25 text-xs mt-10">
              © {new Date().getFullYear()} CEDO Trace — Tous droits réservés
            </p>
          )}
        </div>
      </div>

      {/* ── Right panel — Animated slideshow ────────────────────────────────── */}
      <div className={`hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br ${slide.gradient}`}>
        {/* Animated blob background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-black/10 rounded-full blur-3xl animate-pulse delay-1000" />
          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-16 w-full">
          {/* Top: spacer */}
          <div />

          {/* Center: slide content */}
          <div className="flex-1 flex flex-col justify-center">
            {/* Slide icon */}
            <div
              key={activeSlide}
              className="mb-8 transition-all duration-500"
              style={{ animation: 'fadeSlideIn 0.5s ease-out' }}
            >
              <div className="w-20 h-20 rounded-3xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center mb-8 shadow-2xl">
                <SlideIcon className="w-10 h-10 text-white" />
              </div>

              <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
                {slide.title}
              </h2>
              <p className="text-white/75 text-lg leading-relaxed max-w-sm">
                {slide.description}
              </p>
            </div>

            {/* Stats chips */}
            <div className="flex flex-wrap gap-3">
              {[
                ['📦', 'Multi-tenant', 'Architecture'],
                ['🔒', isEn ? 'JWT Auth' : 'Auth JWT', isEn ? 'Secure' : 'Sécurisé'],
                ['📊', isEn ? 'Real-time' : 'Temps réel', 'Dashboard'],
              ].map(([emoji, label, sub], i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20">
                  <span className="text-base">{emoji}</span>
                  <div>
                    <p className="text-white text-xs font-semibold leading-none">{label}</p>
                    <p className="text-white/50 text-xs">{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom: slide indicators */}
          <div className="flex items-center gap-2">
            {slidesData.map((_, i) => (
              <button
                key={i}
                type="button"
                title={`Slide ${i + 1}`}
                onClick={() => setActiveSlide(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === activeSlide ? 'w-8 bg-white' : 'w-2 bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
